<template>
  <div class="tp-landing" :class="{ 'tp-landing--dark': dark }">
    <!-- ============ HEADER ============ -->
    <header class="tp-header" :class="{ 'tp-header--scrolled': scrolled }">
      <div class="tp-container tp-header__inner">
        <a href="#topo" class="tp-logo" @click.prevent="scrollTo('topo')">
          <img :src="dark ? require('../../assets/logoTemplusBranca.png') : require('../../assets/logoTemplus.png')" alt="Templus" />
        </a>

        <nav class="tp-nav" :class="{ 'tp-nav--open': menuAberto }">
          <a href="#recursos" @click.prevent="scrollTo('recursos')">Recursos</a>
          <a href="#relatorios" @click.prevent="scrollTo('relatorios')">Relatórios</a>
          <a href="#como-funciona" @click.prevent="scrollTo('como-funciona')">Como funciona</a>
          <a href="#planos" @click.prevent="scrollTo('planos')">Planos</a>
          <button class="tp-theme-toggle" @click="toggleTema" :title="dark ? 'Tema claro' : 'Tema escuro'" aria-label="Alternar tema">
            <v-icon small>{{ dark ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
          </button>
          <router-link to="/login" class="tp-nav__entrar">Entrar</router-link>
          <router-link to="/criar-conta" class="tp-btn tp-btn--primary tp-nav__cta">
            <v-icon small left>mdi-account-plus</v-icon>
            Criar conta
          </router-link>
        </nav>

        <button class="tp-burger" @click="menuAberto = !menuAberto" aria-label="Menu">
          <v-icon :color="dark ? 'white' : '#22201E'">{{ menuAberto ? 'mdi-close' : 'mdi-menu' }}</v-icon>
        </button>
      </div>
    </header>

    <!-- ============ HERO ============ -->
    <section id="topo" class="tp-hero">
      <div class="tp-container tp-hero__grid">
        <div class="tp-hero__copy tp-reveal">
          <span class="tp-pill">
            <v-icon x-small color="#00695C">mdi-flash</v-icon>
            Plataforma 100% digital para a sua igreja
          </span>
          <h1 class="tp-display">
            Toda a sua igreja <span class="tp-accent">organizada</span> em um só lugar
          </h1>
          <p class="tp-lead">
            Células, membros, escola bíblica, eventos, check-in e relatórios — uma
            plataforma simples e moderna para você cuidar de pessoas, não de planilhas.
          </p>
          <div class="tp-hero__actions">
            <router-link to="/criar-conta" class="tp-btn tp-btn--primary tp-btn--lg">
              <v-icon left>mdi-rocket-launch</v-icon>
              Criar conta grátis
            </router-link>
            <router-link to="/login" class="tp-btn tp-btn--ghost tp-btn--lg">
              Já tenho conta
              <v-icon right>mdi-arrow-right</v-icon>
            </router-link>
          </div>

          <div class="tp-trust">
            <span><v-icon x-small color="#00695C">mdi-shield-check</v-icon> Dados em nuvem</span>
            <span><v-icon x-small color="#00695C">mdi-lock</v-icon> Conforme a LGPD</span>
          </div>
        </div>

        <!-- Ilustração da igreja + comunidade -->
        <div class="tp-hero__art tp-reveal">
          <img :src="require('../../assets/hero-igreja.svg')" alt="Igreja e sua comunidade" class="tp-hero__img" />
        </div>
      </div>
    </section>

    <!-- ============ RECURSOS ============ -->
    <section id="recursos" class="tp-section">
      <div class="tp-container">
        <div class="tp-section__head tp-reveal">
          <span class="tp-eyebrow">TUDO EM UM SÓ LUGAR</span>
          <h2 class="tp-display tp-display--md">Tudo que a sua igreja precisa</h2>
          <p class="tp-section__sub">Uma plataforma completa para organizar pessoas, ministérios e eventos.</p>
        </div>

        <div class="tp-cards">
          <div
            v-for="(r, i) in recursos"
            :key="r.titulo"
            class="tp-card tp-reveal"
            :class="{ 'tp-card--feature': r.destaque }"
            :style="{ transitionDelay: (i * 60) + 'ms' }"
          >
            <div class="tp-card__icon">
              <v-icon color="white">{{ r.icone }}</v-icon>
            </div>
            <h3>{{ r.titulo }}</h3>
            <p>{{ r.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ SPOTLIGHT: DASHBOARD & RELATÓRIOS ============ -->
    <section id="relatorios" class="tp-section tp-section--alt">
      <div class="tp-container tp-spot">
        <div class="tp-spot__copy tp-reveal">
          <span class="tp-pill"><v-icon x-small color="#00695C">mdi-chart-box</v-icon> DASHBOARD & RELATÓRIOS</span>
          <h2 class="tp-display tp-display--md">Sua igreja em <span class="tp-accent">números claros</span></h2>
          <p class="tp-lead">
            Membros, células, presenças e crescimento — atualizados em tempo real, sem planilha.
          </p>
          <ul class="tp-bullets">
            <li>
              <span class="tp-bullet__ic"><v-icon small color="#00695C">mdi-monitor-dashboard</v-icon></span>
              <div><strong>Tudo em tempo real</strong><p>Membros, congregados, células e presenças sempre atualizados.</p></div>
            </li>
            <li>
              <span class="tp-bullet__ic"><v-icon small color="#00695C">mdi-chart-donut</v-icon></span>
              <div><strong>Visão por rede e célula</strong><p>A distribuição de pessoas em cada rede, célula e ministério.</p></div>
            </li>
            <li>
              <span class="tp-bullet__ic"><v-icon small color="#00695C">mdi-chart-timeline-variant</v-icon></span>
              <div><strong>Crescimento que dá pra ver</strong><p>Tendências por gênero, faixa etária e evolução no tempo.</p></div>
            </li>
          </ul>
          <router-link to="/login" class="tp-btn tp-btn--primary tp-btn--lg">
            <v-icon left>mdi-chart-box</v-icon>
            Ver o dashboard
          </router-link>
        </div>

        <div class="tp-spot__art tp-reveal">
          <div class="tp-report">
            <div class="tp-report__head">
              <span class="tp-report__title">Painel da igreja</span>
              <span class="tp-report__live"><i></i> Ao vivo</span>
            </div>

            <div class="tp-report__row">
              <div class="tp-report__kpi"><p class="tp-report__num">1.248</p><p class="tp-report__lbl">Membros</p></div>
              <div class="tp-report__kpi"><p class="tp-report__num">86</p><p class="tp-report__lbl">Células</p></div>
              <div class="tp-report__kpi"><p class="tp-report__num">24</p><p class="tp-report__lbl">Ministérios</p></div>
            </div>

            <span class="tp-report__chart-label">Membros por rede</span>
            <div class="tp-report__donutwrap">
              <div class="tp-donut tp-donut--sm">
                <div class="tp-donut__hole"><strong>1.248</strong><span>membros</span></div>
              </div>
              <div class="tp-report__legend tp-report__legend--v">
                <span><i style="background:#5BC1A3"></i> Rede Norte · 38%</span>
                <span><i style="background:#01B094"></i> Rede Sul · 34%</span>
                <span><i style="background:#00695C"></i> Rede Leste · 28%</span>
              </div>
            </div>

            <span class="tp-report__chart-label">Novos membros por mês</span>
            <div class="tp-report__bars">
              <div v-for="(h, i) in barras" :key="i" class="tp-report__bar" :style="{ height: h + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ COMO FUNCIONA ============ -->
    <section id="como-funciona" class="tp-section">
      <div class="tp-container">
        <div class="tp-section__head tp-reveal">
          <span class="tp-eyebrow">SIMPLES ASSIM</span>
          <h2 class="tp-display tp-display--md">Comece em 3 passos</h2>
        </div>

        <div class="tp-steps">
          <div v-for="(p, i) in passos" :key="p.titulo" class="tp-step tp-reveal" :style="{ transitionDelay: (i * 80) + 'ms' }">
            <div class="tp-step__num">{{ i + 1 }}</div>
            <h3>{{ p.titulo }}</h3>
            <p>{{ p.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ FAIXA DE SEGURANÇA ============ -->
    <section class="tp-section tp-section--alt tp-section--tight">
      <div class="tp-container tp-secure">
        <div v-for="s in seguranca" :key="s.titulo" class="tp-secure__item tp-reveal">
          <span class="tp-secure__ic"><v-icon color="#00695C">{{ s.icone }}</v-icon></span>
          <div>
            <strong>{{ s.titulo }}</strong>
            <small>{{ s.sub }}</small>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ PLANOS ============ -->
    <section id="planos" class="tp-section tp-section--alt">
      <div class="tp-container">
        <div class="tp-section__head tp-reveal">
          <span class="tp-pill"><v-icon x-small color="#00695C">mdi-tag-outline</v-icon> Planos</span>
          <h2 class="tp-display tp-display--md">O valor varia conforme o porte da sua igreja</h2>
          <p class="tp-section__lead">Escolha a sua igreja e comece agora. Sem fidelidade — você ajusta a faixa quando crescer.</p>
        </div>
        <div class="tp-planos">
          <div v-for="p in planos" :key="p.id" class="tp-plano tp-reveal" :class="{ 'tp-plano--popular': p.popular }">
            <span v-if="p.popular" class="tp-plano__badge">POPULAR</span>
            <h3 class="tp-plano__faixa">{{ p.faixa }}</h3>
            <p class="tp-plano__sub">{{ p.subtitulo }}</p>
            <div class="tp-plano__valor">
              <span class="tp-plano__num">{{ formatarValor(p.valor) }}</span>
              <span class="tp-plano__sufixo">{{ p.sufixo }}</span>
            </div>
            <ul class="tp-plano__list">
              <li v-for="r in recursosPlano" :key="r"><v-icon small color="#01B094">mdi-check</v-icon>{{ r }}</li>
            </ul>
            <router-link :to="{ path: '/criar-conta', query: { plano: p.id } }" class="tp-btn tp-plano__cta" :class="p.popular ? 'tp-btn--primary' : 'tp-btn--ghost'">
              Criar conta
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ CTA FINAL ============ -->
    <section id="seguranca" class="tp-section">
      <div class="tp-container">
        <div class="tp-cta tp-reveal">
          <div class="tp-cta__content">
            <h2 class="tp-display tp-display--md tp-cta__title">Pronto para transformar a gestão da sua igreja?</h2>
            <p>Crie sua conta, aceite o contrato digital e comece a usar em minutos.</p>
            <router-link to="/criar-conta" class="tp-btn tp-btn--dark tp-btn--lg">
              <v-icon left color="#00695C">mdi-rocket-launch</v-icon>
              Criar conta agora
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ FOOTER ============ -->
    <footer class="tp-footer">
      <div class="tp-container tp-footer__grid">
        <div class="tp-footer__brand">
          <img src="../../assets/logoTemplusBranca.png" alt="Templus" />
          <p>Plataforma de gestão para igrejas: pessoas, células, escola bíblica, eventos e ação social — tudo em um só lugar.</p>
        </div>
        <div class="tp-footer__col">
          <h4>Produto</h4>
          <a href="#recursos" @click.prevent="scrollTo('recursos')">Recursos</a>
          <a href="#relatorios" @click.prevent="scrollTo('relatorios')">Relatórios</a>
          <router-link to="/login">Entrar</router-link>
        </div>
        <div class="tp-footer__col">
          <h4>Fale com a gente</h4>
          <a href="https://api.whatsapp.com/send/?phone=558335125222" target="_blank" rel="noopener">WhatsApp</a>
          <a href="https://www.instagram.com/intelite_tecnologia/" target="_blank" rel="noopener">Instagram</a>
          <a href="https://www.intelitetecnologia.com.br/" target="_blank" rel="noopener">Site Intelite</a>
        </div>
      </div>
      <div class="tp-container tp-footer__bottom">
        <span>© {{ ano }} Templus · Todos os direitos reservados</span>
        <span>Feito com cuidado no Brasil 🇧🇷</span>
      </div>
      <div class="tp-footer__intelite">
        <span>Uma solução</span>
        <img src="../../assets/logoTemplusIntelite.png" alt="Intelite Tecnologia" />
      </div>
    </footer>
  </div>
</template>

<script>
import { PLANOS, RECURSOS_PLANO, formatBRL } from '@/plans'

export default {
  name: 'Landing',
  data() {
    return {
      planos: PLANOS,
      recursosPlano: RECURSOS_PLANO,
      scrolled: false,
      menuAberto: false,
      dark: false,
      ano: new Date().getFullYear(),
      barras: [40, 62, 50, 78, 66, 90],
      observer: null,
      recursos: [
        { icone: 'mdi-teddy-bear', titulo: 'Escola Bíblica & Kids', texto: 'Turmas por faixa etária, matrículas e acompanhamento da frequência dos alunos.' },
        { icone: 'mdi-sitemap', titulo: 'Redes & Células', texto: 'Organize redes, supervisões e células com líderes, frequência e relatórios.' },
        { icone: 'mdi-account-group', titulo: 'Membros & Congregados', texto: 'Cadastro completo de pessoas, com histórico, família e dados eclesiásticos.' },
        { icone: 'mdi-account-multiple-plus', titulo: 'Cadastro de novos membros', texto: 'Links públicos para que novas pessoas se cadastrem direto na sua igreja.' },
        { icone: 'mdi-hand-heart', titulo: 'Voluntários & Ministérios', texto: 'Escale ministérios, funções e setores com poucos cliques.' },
        { icone: 'mdi-chart-box', titulo: 'Relatórios em tempo real', texto: 'Painéis e indicadores para acompanhar o crescimento da sua igreja.' },
      ],
      passos: [
        { titulo: 'Crie sua conta', texto: 'Cadastre-se, aceite o contrato digital e ative sua igreja na hora.' },
        { titulo: 'Configure sua igreja', texto: 'Adicione redes, células, ministérios e turmas do seu jeito.' },
        { titulo: 'Gerencie tudo', texto: 'Acompanhe pessoas, presenças e indicadores em um painel só.' },
      ],
      seguranca: [
        { icone: 'mdi-lock-check', titulo: 'Site seguro', sub: 'Conexão criptografada SSL/TLS' },
        { icone: 'mdi-cloud-check', titulo: 'Em nuvem', sub: 'Backups e disponibilidade' },
        { icone: 'mdi-shield-account', titulo: 'Dados protegidos', sub: 'Conforme a LGPD' },
      ],
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.dark = localStorage.getItem('tp-landing-theme') === 'dark'
    this.initReveal()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    formatarValor(v) {
      return formatBRL(v)
    },
    onScroll() {
      this.scrolled = window.scrollY > 12
    },
    scrollTo(id) {
      this.menuAberto = false
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    toggleTema() {
      this.dark = !this.dark
      localStorage.setItem('tp-landing-theme', this.dark ? 'dark' : 'light')
    },
    initReveal() {
      const els = this.$el.querySelectorAll('.tp-reveal')
      if (!('IntersectionObserver' in window)) {
        els.forEach(el => el.classList.add('tp-reveal--in'))
        return
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('tp-reveal--in')
            this.observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.12 })
      els.forEach(el => this.observer.observe(el))
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

.tp-landing {
  --tp-teal: #5BC1A3;       /* principal (brilho) */
  --tp-teal-2: #01B094;
  --tp-gold: #5BC1A3;       /* secundária */
  --tp-panel: #ffffff;
  --tp-panel-2: #ffffff;
  --tp-border: rgba(0,37,62,.08);
  --tp-muted: #7a726d;
  --tp-ink: #22201E;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--tp-ink);
  background:
    radial-gradient(1100px 620px at 88% -6%, rgba(0,105,92,.05), transparent 62%),
    #FBF7F5;
  overflow-x: hidden;
}

.tp-container { width: 100%; max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* ---------- Tipografia ---------- */
.tp-display {
  font-family: 'Fraunces', Georgia, serif;
  font-weight: 600; line-height: 1.07; letter-spacing: -0.015em;
  font-size: clamp(2.3rem, 5vw, 3.7rem); margin: 0 0 18px; color: var(--tp-ink);
}
.tp-display--md { font-size: clamp(1.9rem, 3.6vw, 2.7rem); }
.tp-accent { font-style: italic; color: #00695C; }
.tp-lead { font-size: 1.12rem; color: var(--tp-muted); line-height: 1.6; max-width: 32em; margin: 0 0 30px; }
.tp-eyebrow { display: inline-block; font-size: .78rem; font-weight: 700; letter-spacing: .14em; color: #00695C; margin-bottom: 14px; }

/* pill badge */
.tp-pill {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(0,105,92,.08); border: 1px solid rgba(0,105,92,.18);
  color: #00695C; font-weight: 600; font-size: .8rem; letter-spacing: .02em;
  padding: 7px 14px; border-radius: 999px; margin-bottom: 22px;
}

/* ---------- Botões ---------- */
.tp-btn {
  display: inline-flex; align-items: center; gap: 8px; justify-content: center;
  font-weight: 600; font-size: .95rem; text-decoration: none; cursor: pointer;
  border-radius: 999px; padding: 11px 22px; transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  border: 1.5px solid transparent;
}
.tp-btn--lg { padding: 15px 30px; font-size: 1rem; }
.tp-btn--primary { background: #00695C; color: #fff; box-shadow: 0 12px 28px rgba(0,105,92,.26); }
.tp-btn--primary:hover { transform: translateY(-2px); filter: brightness(1.06); box-shadow: 0 16px 34px rgba(0,105,92,.32); }
.tp-btn--primary .v-icon { color: #fff !important; }
.tp-btn--ghost { background: #fff; color: #00695C; border-color: rgba(0,37,62,.14); }
.tp-btn--ghost:hover { border-color: #00695C; transform: translateY(-2px); }
.tp-btn--ghost .v-icon { color: #00695C !important; }
.tp-btn--dark { background: #00695C; color: #fff; box-shadow: 0 12px 28px rgba(0,105,92,.26); }
.tp-btn--dark:hover { transform: translateY(-2px); filter: brightness(1.06); }
.tp-btn--dark .v-icon { color: #fff !important; }

/* ---------- Header ---------- */
.tp-header {
  position: sticky; top: 0; z-index: 50;
  background: rgba(251,247,245,.75); backdrop-filter: blur(12px);
  transition: box-shadow .2s ease, background .2s ease; border-bottom: 1px solid transparent;
}
.tp-header--scrolled { background: rgba(255,255,255,.94); box-shadow: 0 6px 24px rgba(0,37,62,.06); border-bottom-color: rgba(0,37,62,.06); }
.tp-header__inner { display: flex; align-items: center; justify-content: space-between; height: 74px; }
.tp-logo img { height: 36px; display: block; }
.tp-nav { display: flex; align-items: center; gap: 28px; }
.tp-nav > a:not(.tp-btn) { color: #3a3430; text-decoration: none; font-weight: 500; font-size: .95rem; }
.tp-nav > a:not(.tp-btn):hover { color: #00695C; }
.tp-nav__entrar { font-weight: 600 !important; }
.tp-burger { display: none; background: none; border: none; padding: 6px; cursor: pointer; }

/* ---------- Hero ---------- */
.tp-hero { position: relative; padding: 80px 0 96px; }
.tp-hero__grid { display: grid; grid-template-columns: 1.05fr .95fr; gap: 56px; align-items: center; }
.tp-hero__actions { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 30px; }
.tp-trust { display: flex; gap: 22px; flex-wrap: wrap; color: var(--tp-muted); font-size: .86rem; font-weight: 500; }
.tp-trust span { display: inline-flex; align-items: center; gap: 6px; }

/* ilustração do hero */
.tp-hero__art { position: relative; display: flex; justify-content: center; }
.tp-hero__img { width: 100%; max-width: 520px; height: auto; display: block; filter: drop-shadow(0 22px 44px rgba(0,37,62,.14)); }

/* preview mock (legado — mantido para referência) */
.tp-preview {
  background: linear-gradient(180deg, #26201d, #17120f); border: 1px solid var(--tp-border); border-radius: 18px;
  box-shadow: 0 36px 80px rgba(0,0,0,.45); overflow: hidden; position: relative;
}
.tp-preview__bar { display: flex; align-items: center; gap: 7px; padding: 13px 16px; background: rgba(0,0,0,.25); }
.tp-preview__bar span { width: 11px; height: 11px; border-radius: 50%; }
.tp-preview__bar span:first-child { background: #ff6b5e; }
.tp-preview__bar span:nth-child(2) { background: #ffbe4f; }
.tp-preview__bar span:nth-child(3) { background: #43c97f; }
.tp-preview__title { color: rgba(255,255,255,.6); font-size: .78rem; margin: 0 0 0 10px; font-weight: 500; }
.tp-preview__body { padding: 22px; }
.tp-preview__kpis { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin-bottom: 20px; }
.tp-kpi { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.10); border-radius: 12px; padding: 14px 10px; text-align: center; }
.tp-kpi__num { font-family: 'Fraunces', serif; font-size: 1.45rem; font-weight: 700; color: var(--tp-teal); margin: 0; }
.tp-kpi__label { font-size: .7rem; color: rgba(255,255,255,.62); margin: 2px 0 0; }
.tp-preview__chart-label { font-size: .74rem; color: rgba(255,255,255,.62); font-weight: 500; }
.tp-preview__bars { display: flex; align-items: flex-end; gap: 10px; height: 120px; padding-top: 10px; }
.tp-bar { flex: 1; background: linear-gradient(180deg, var(--tp-teal), var(--tp-teal-2)); border-radius: 6px 6px 0 0; min-height: 8px; }

.tp-toast {
  position: absolute; display: flex; align-items: center; gap: 11px;
  background: #fff; border: 1px solid rgba(0,37,62,.08); border-radius: 14px;
  padding: 11px 15px; box-shadow: 0 18px 42px rgba(0,37,62,.16);
}
.tp-toast__ic { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; }
.tp-toast__ic--green { background: #2E9E78; }
.tp-toast__ic--teal { background: var(--tp-teal-2); }
.tp-toast strong { display: block; font-size: .85rem; color: #22201E; }
.tp-toast small { color: var(--tp-muted); font-size: .74rem; }
.tp-toast--a { bottom: -22px; left: -18px; animation: floaty 4s ease-in-out infinite; }
.tp-toast--b { top: -20px; right: -10px; animation: floaty 4.6s ease-in-out infinite .6s; }
@keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

/* ---------- Sections ---------- */
.tp-section { padding: 88px 0; }
.tp-section--alt { background: #F5F0EC; border-top: 1px solid rgba(34,32,30,.06); border-bottom: 1px solid rgba(34,32,30,.06); }
.tp-section--tight { padding: 44px 0; }
.tp-section__head { text-align: center; max-width: 640px; margin: 0 auto 50px; }
.tp-section__head .tp-eyebrow { display: block; }
.tp-section__sub { color: #7a726d; font-size: 1.05rem; margin: 6px 0 0; }
.tp-section__lead { color: #7a726d; font-size: 1.05rem; margin: 10px 0 0; }

/* Planos */
.tp-planos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-items: stretch; }
.tp-plano {
  position: relative; display: flex; flex-direction: column;
  background: var(--tp-panel); border: 1px solid var(--tp-border); border-radius: 22px; padding: 30px 26px;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.tp-plano:hover { transform: translateY(-5px); box-shadow: 0 24px 50px rgba(0,37,62,.16); }
.tp-plano--popular {
  border-color: var(--tp-teal);
  box-shadow: 0 22px 48px rgba(1,176,148,.20);
}
.tp-plano__badge {
  position: absolute; top: -13px; left: 26px;
  background: linear-gradient(135deg, var(--tp-teal), var(--tp-primary));
  color: #fff; font-size: .72rem; font-weight: 700; letter-spacing: .08em;
  padding: 5px 14px; border-radius: 999px; box-shadow: 0 6px 16px rgba(1,176,148,.35);
}
.tp-plano__faixa { font-size: 1.2rem; color: #22201E; margin: 0 0 4px; font-weight: 700; }
.tp-plano__sub { color: #7a726d; font-size: .92rem; margin: 0 0 18px; }
.tp-plano__valor { display: flex; align-items: baseline; gap: 6px; margin-bottom: 22px; }
.tp-plano__num { font-family: 'Fraunces', Georgia, serif; font-weight: 700; font-size: 2.4rem; color: var(--tp-primary); line-height: 1; }
.tp-plano__sufixo { color: #7a726d; font-size: .95rem; }
.tp-plano__list { list-style: none; padding: 0; margin: 0 0 26px; display: grid; gap: 12px; }
.tp-plano__list li { display: flex; align-items: center; gap: 10px; color: #2f3e3a; font-size: .95rem; }
.tp-plano__cta { width: 100%; justify-content: center; margin-top: auto; }
.tp-landing--dark .tp-plano { background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.10); }
.tp-landing--dark .tp-plano--popular { background: rgba(1,176,148,.10); border-color: var(--tp-teal); box-shadow: 0 22px 48px rgba(0,0,0,.45); }
.tp-landing--dark .tp-plano:hover { box-shadow: 0 24px 50px rgba(0,0,0,.5); }
.tp-landing--dark .tp-plano__faixa { color: #eaf2ef; }
.tp-landing--dark .tp-plano__sub { color: rgba(255,255,255,.6); }
.tp-landing--dark .tp-plano__num { color: #5BC1A3; }
.tp-landing--dark .tp-plano__sufixo { color: rgba(255,255,255,.6); }
.tp-landing--dark .tp-plano__list li { color: rgba(255,255,255,.82); }
.tp-landing--dark .tp-section__lead { color: rgba(255,255,255,.7); }

.tp-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.tp-card {
  position: relative; background: var(--tp-panel); border: 1px solid var(--tp-border); border-radius: 20px; padding: 30px 28px;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease; overflow: hidden;
}
.tp-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--tp-teal), var(--tp-gold)); transform: scaleX(0); transform-origin: left; transition: transform .25s ease;
}
.tp-card:hover { transform: translateY(-5px); background: var(--tp-panel-2); border-color: transparent; box-shadow: 0 24px 50px rgba(0,37,62,.16); }
.tp-card:hover::before { transform: scaleX(1); }
.tp-card__icon {
  width: 54px; height: 54px; border-radius: 15px; display: grid; place-items: center;
  background: linear-gradient(135deg, var(--tp-teal), var(--tp-teal-2)); margin-bottom: 18px; box-shadow: 0 8px 18px rgba(1,176,148,.35);
}
.tp-card h3 { font-size: 1.16rem; margin: 0 0 8px; color: #22201E; }
.tp-card p { color: #7a726d; font-size: .95rem; line-height: 1.55; margin: 0; }
.tp-card--feature { background: linear-gradient(160deg, #ffffff, #e9f8f2); border-color: rgba(0,105,92,.22); }
.tp-card--feature::before { transform: scaleX(1); }
.tp-tag { position: absolute; top: 18px; right: 18px; font-size: .64rem; font-weight: 700; letter-spacing: .1em; color: #00253E; background: var(--tp-teal); padding: 4px 9px; border-radius: 999px; }

/* spotlight kids */
.tp-spot { display: grid; grid-template-columns: 1.05fr .95fr; gap: 56px; align-items: center; }
.tp-bullets { list-style: none; padding: 0; margin: 0 0 30px; display: grid; gap: 18px; }
.tp-bullets li { display: flex; gap: 14px; align-items: flex-start; }
.tp-bullet__ic { flex: none; width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; background: rgba(91,193,163,.12); border: 1px solid rgba(91,193,163,.25); }
.tp-bullets strong { display: block; color: #22201E; font-size: 1rem; }
.tp-bullets p { margin: 2px 0 0; color: var(--tp-muted); font-size: .92rem; line-height: 1.5; }
.tp-spot__art { display: flex; flex-direction: column; align-items: center; gap: 16px; }
.tp-scan {
  position: relative; width: 280px; height: 280px; max-width: 100%;
  display: grid; place-items: center; border-radius: 22px;
  background: radial-gradient(circle at 50% 40%, rgba(91,193,163,.14), transparent 70%);
}
.tp-scan__c { position: absolute; width: 38px; height: 38px; border: 3px solid var(--tp-teal); }
.tp-scan__c--tl { top: 16px; left: 16px; border-right: 0; border-bottom: 0; border-radius: 8px 0 0 0; }
.tp-scan__c--tr { top: 16px; right: 16px; border-left: 0; border-bottom: 0; border-radius: 0 8px 0 0; }
.tp-scan__c--bl { bottom: 16px; left: 16px; border-right: 0; border-top: 0; border-radius: 0 0 0 8px; }
.tp-scan__c--br { bottom: 16px; right: 16px; border-left: 0; border-top: 0; border-radius: 0 0 8px 0; }
.tp-scan__line { position: absolute; left: 30px; right: 30px; height: 2px; background: linear-gradient(90deg, transparent, var(--tp-teal), transparent); animation: scanmove 2.6s ease-in-out infinite; }
@keyframes scanmove { 0%,100% { top: 40px; } 50% { top: 230px; } }
.tp-scan__status { color: var(--tp-muted); font-size: .86rem; display: inline-flex; align-items: center; gap: 6px; }

/* mock de relatório (spotlight dashboard) */
.tp-report { width: 100%; max-width: 440px; background: #fff; border: 1px solid rgba(0,37,62,.08); border-radius: 18px; padding: 22px; box-shadow: 0 26px 60px rgba(0,37,62,.22); }
.tp-report__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.tp-report__title { font-family: 'Fraunces', serif; font-weight: 600; font-size: 1.05rem; color: #22201E; }
.tp-report__live { display: inline-flex; align-items: center; gap: 6px; font-size: .72rem; font-weight: 600; color: #1E7A5E; background: #E9F6F0; border-radius: 999px; padding: 4px 10px; }
.tp-report__live i { width: 7px; height: 7px; border-radius: 50%; background: #2E9E78; display: inline-block; animation: tp-pulse 1.6s ease-in-out infinite; }
@keyframes tp-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.75); } }
.tp-report__donutwrap { display: flex; align-items: center; gap: 18px; margin: 6px 0 6px; }
.tp-report__legend--v { flex-direction: column; align-items: flex-start; gap: 10px; margin-top: 0; }
.tp-report__row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px; }
.tp-report__kpi { background: #F4F7F6; border: 1px solid rgba(0,37,62,.06); border-radius: 12px; padding: 12px 8px; text-align: center; }
.tp-report__num { font-family: 'Fraunces', serif; font-weight: 700; font-size: 1.35rem; color: #00695C; margin: 0; }
.tp-report__lbl { font-size: .68rem; color: #7a726d; margin: 2px 0 0; }
.tp-report__chart-label { font-size: .74rem; color: #7a726d; font-weight: 500; }
.tp-report__bars { display: flex; align-items: flex-end; gap: 9px; height: 120px; padding-top: 10px; }
.tp-report__bar { flex: 1; background: linear-gradient(180deg, #5BC1A3, #01B094); border-radius: 6px 6px 0 0; min-height: 8px; }
.tp-report__legend { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 16px; }
.tp-report__legend span { display: inline-flex; align-items: center; gap: 6px; font-size: .74rem; color: #7a726d; }
.tp-report__legend i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
.tp-donut { position: relative; width: 210px; height: 210px; margin: 16px auto 8px; border-radius: 50%; background: conic-gradient(#5BC1A3 0 38%, #01B094 38% 72%, #00695C 72% 100%); }
.tp-donut__hole { position: absolute; inset: 32px; background: #fff; border-radius: 50%; display: grid; place-items: center; text-align: center; box-shadow: inset 0 1px 4px rgba(0,37,62,.08); }
.tp-donut__hole strong { font-family: 'Fraunces', serif; font-size: 1.7rem; color: #00695C; display: block; line-height: 1; }
.tp-donut__hole span { font-size: .74rem; color: #7a726d; }
.tp-donut--sm { width: 136px; height: 136px; margin: 0; flex: none; }
.tp-donut--sm .tp-donut__hole { inset: 22px; }
.tp-donut--sm .tp-donut__hole strong { font-size: 1.2rem; }
.tp-donut--sm .tp-donut__hole span { font-size: .66rem; }

/* steps */
.tp-steps { display: grid; grid-template-columns: repeat(3,1fr); gap: 22px; }
.tp-step { background: var(--tp-panel); border: 1px solid var(--tp-border); border-radius: 20px; padding: 32px 28px; }
.tp-step__num {
  width: 44px; height: 44px; border-radius: 13px; background: linear-gradient(135deg, var(--tp-teal), var(--tp-teal-2)); color: #00253E;
  font-family: 'Fraunces', serif; font-weight: 700; font-size: 1.25rem; display: grid; place-items: center; margin-bottom: 18px; box-shadow: 0 8px 18px rgba(1,176,148,.3);
}
.tp-step h3 { margin: 0 0 8px; font-size: 1.16rem; color: #22201E; }
.tp-step p { color: #7a726d; margin: 0; line-height: 1.55; }

/* faixa de segurança */
.tp-secure { display: grid; grid-template-columns: repeat(3,1fr); gap: 22px; }
.tp-secure__item { display: flex; align-items: center; gap: 14px; }
.tp-secure__ic { flex: none; width: 46px; height: 46px; border-radius: 12px; display: grid; place-items: center; background: rgba(91,193,163,.10); border: 1px solid rgba(91,193,163,.22); }
.tp-secure__item strong { display: block; color: #22201E; font-size: .98rem; }
.tp-secure__item small { color: var(--tp-muted); font-size: .8rem; }

/* cta */
.tp-cta {
  position: relative; overflow: hidden; border-radius: 28px; padding: 70px 40px; text-align: center;
  background: linear-gradient(135deg, #00493F 0%, #00695C 60%, #0B7E6B 100%); color: #fff;
}
.tp-cta::after { content: ''; position: absolute; width: 520px; height: 300px; right: -80px; top: -120px; background: radial-gradient(circle, rgba(255,255,255,.18), transparent 60%); }
.tp-cta__content { position: relative; z-index: 1; max-width: 640px; margin: 0 auto; }
.tp-cta__title { color: #fff; margin-bottom: 12px; }
.tp-cta p { color: rgba(255,255,255,.88); font-size: 1.08rem; margin: 0 0 28px; font-weight: 500; }
/* botão branco para destacar sobre o verde */
.tp-cta .tp-btn--dark { background: #fff; color: #00695C; box-shadow: 0 12px 28px rgba(0,0,0,.18); }
.tp-cta .tp-btn--dark:hover { filter: none; background: #f3faf8; }
.tp-cta .tp-btn--dark .v-icon { color: #00695C !important; }

/* footer */
.tp-footer { background: #00342D; border-top: 1px solid var(--tp-border); padding: 58px 0 26px; color: rgba(255,255,255,.75); }
.tp-footer__grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 40px; }
.tp-footer__brand img { height: 34px; margin-bottom: 16px; }
.tp-footer__brand p { font-size: .92rem; line-height: 1.6; max-width: 34em; }
.tp-footer__col h4 { color: #fff; font-size: .95rem; margin: 0 0 14px; }
.tp-footer__col a { display: block; color: rgba(255,255,255,.72); text-decoration: none; font-size: .92rem; margin-bottom: 10px; }
.tp-footer__col a:hover { color: var(--tp-teal); }
.tp-footer__bottom {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px;
  border-top: 1px solid var(--tp-border); margin-top: 42px; padding-top: 22px; font-size: .82rem; color: rgba(255,255,255,.5);
}
.tp-footer__intelite { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 26px; opacity: .85; }
.tp-footer__intelite span { font-size: .82rem; color: rgba(255,255,255,.55); }
.tp-footer__intelite img { height: 26px; }

/* reveal */
.tp-reveal { opacity: 0; transform: translateY(22px); transition: opacity .6s ease, transform .6s ease; }
.tp-reveal--in { opacity: 1; transform: none; }

/* responsivo */
@media (max-width: 900px) {
  .tp-hero__grid, .tp-spot { grid-template-columns: 1fr; gap: 56px; }
  .tp-hero__art { order: -1; }
  .tp-cards, .tp-steps { grid-template-columns: 1fr 1fr; }
  .tp-secure { grid-template-columns: 1fr 1fr; }
  .tp-footer__grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 720px) {
  .tp-burger { display: block; }
  .tp-nav {
    position: absolute; top: 74px; left: 0; right: 0; flex-direction: column; align-items: stretch; gap: 6px;
    background: #fff; padding: 16px 24px 22px; box-shadow: 0 16px 30px rgba(0,37,62,.12);
    transform: translateY(-12px); opacity: 0; pointer-events: none; transition: .18s ease;
  }
  .tp-nav--open { transform: translateY(0); opacity: 1; pointer-events: auto; }
  .tp-nav__cta { justify-content: center; margin-top: 6px; }
  .tp-cards, .tp-steps, .tp-secure, .tp-footer__grid, .tp-planos { grid-template-columns: 1fr; }
  .tp-cta { padding: 52px 24px; }
}

/* ---------- Toggle de tema ---------- */
.tp-theme-toggle {
  background: none; border: none; cursor: pointer; padding: 6px; display: inline-flex;
  align-items: center; color: #2b3a36; transition: color .15s ease;
}
/* o v-icon não herda o color do botão por padrão — forçamos para não sumir */
.tp-theme-toggle .v-icon { color: inherit !important; }
.tp-theme-toggle:hover { color: #00695C; }

/* ============ MODO ESCURO DA LANDING ============ */
.tp-landing--dark {
  color: #eaf2ef;
  background:
    radial-gradient(1100px 620px at 88% -6%, rgba(0,105,92,.14), transparent 60%),
    linear-gradient(180deg, #1a1519 0%, #100b0e 100%);
}
.tp-landing--dark .tp-display { color: #ffffff; }
.tp-landing--dark .tp-accent { color: #5BC1A3; }
.tp-landing--dark .tp-lead,
.tp-landing--dark .tp-section__sub,
.tp-landing--dark .tp-trust { color: rgba(255,255,255,.72); }
.tp-landing--dark .tp-eyebrow { color: #5BC1A3; }
.tp-landing--dark .tp-pill { background: rgba(91,193,163,.12); border-color: rgba(91,193,163,.30); color: #9fe6ce; }
.tp-landing--dark .tp-pill .v-icon,
.tp-landing--dark .tp-trust .v-icon,
.tp-landing--dark .tp-bullet__ic .v-icon,
.tp-landing--dark .tp-secure__ic .v-icon { color: #5BC1A3 !important; }
.tp-landing--dark .tp-theme-toggle { color: rgba(255,255,255,.85); }
.tp-landing--dark .tp-theme-toggle:hover { color: #5BC1A3; }

/* header */
.tp-landing--dark .tp-header { background: rgba(22,17,20,.6); }
.tp-landing--dark .tp-header--scrolled { background: rgba(18,13,16,.92); box-shadow: 0 6px 24px rgba(0,0,0,.4); border-bottom-color: rgba(255,255,255,.10); }
.tp-landing--dark .tp-nav > a:not(.tp-btn) { color: rgba(255,255,255,.82); }
.tp-landing--dark .tp-nav > a:not(.tp-btn):hover { color: #5BC1A3; }

/* botões */
.tp-landing--dark .tp-btn--ghost { background: rgba(255,255,255,.06); color: #fff; border-color: rgba(255,255,255,.28); }
.tp-landing--dark .tp-btn--ghost:hover { background: rgba(255,255,255,.12); border-color: #fff; }
.tp-landing--dark .tp-btn--ghost .v-icon { color: #fff !important; }

/* cards / seções */
.tp-landing--dark .tp-section--alt { background: rgba(255,255,255,.04); border-top-color: rgba(255,255,255,.08); border-bottom-color: rgba(255,255,255,.08); }
.tp-landing--dark .tp-card { background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.10); }
.tp-landing--dark .tp-card:hover { background: rgba(255,255,255,.08); border-color: rgba(91,193,163,.40); box-shadow: 0 24px 50px rgba(0,0,0,.40); }
.tp-landing--dark .tp-card h3,
.tp-landing--dark .tp-step h3 { color: #fff; }
.tp-landing--dark .tp-card p,
.tp-landing--dark .tp-step p { color: rgba(255,255,255,.66); }
.tp-landing--dark .tp-step { background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.10); }

/* spotlight / segurança */
.tp-landing--dark .tp-bullets strong,
.tp-landing--dark .tp-secure__item strong { color: #fff; }
.tp-landing--dark .tp-bullets p,
.tp-landing--dark .tp-secure__item small { color: rgba(255,255,255,.66); }

/* painel (mock de dashboard) no modo escuro */
.tp-landing--dark .tp-report { background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.10); box-shadow: 0 26px 60px rgba(0,0,0,.45); }
.tp-landing--dark .tp-report__title { color: #eaf2ef; }
.tp-landing--dark .tp-report__kpi { background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.10); }
.tp-landing--dark .tp-report__num { color: #5BC1A3; }
.tp-landing--dark .tp-report__lbl,
.tp-landing--dark .tp-report__chart-label,
.tp-landing--dark .tp-report__legend span { color: rgba(255,255,255,.7); }
.tp-landing--dark .tp-report__live { background: rgba(46,158,120,.18); color: #7fe3bd; }
.tp-landing--dark .tp-donut__hole { background: #0e2a23; box-shadow: none; }
.tp-landing--dark .tp-donut__hole strong { color: #5BC1A3; }
.tp-landing--dark .tp-donut__hole span { color: rgba(255,255,255,.6); }
</style>
